"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ObjectInMap = void 0;
const uuid_1 = require("uuid");
const defaultObjectInMapProps = {
    useAverageLocation: true
};
class ObjectInMap {
    get hasParent() {
        return this.parent !== undefined;
    }
    constructor(map, name = "MapObject", options) {
        this.children = [];
        this.isActive = true;
        this.isInitialized = false;
        this.eventCallbacks = {};
        const props = {
            ...defaultObjectInMapProps,
            ...options
        };
        this.useAverageLocation = props.useAverageLocation;
        this.name = name;
        this.id = (0, uuid_1.v4)();
        // Attach map
        this.map = map;
        this.setMap(map);
        this.location = [0, 0];
        this.setDontForgetTimeout();
    }
    setDontForgetTimeout() {
        this.dontForgetTimeout = setTimeout(() => {
            if (!this.isInitialized)
                console.warn("Don't forget to call initialize() on", this.name, "object. \n" +
                    "It's possible that it contains children that are not initialized yet.");
        }, 5000);
    }
    toString() {
        return this.name;
    }
    getLocation() {
        return this.location;
    }
    setLocation(location) {
        if (this.location[0] === location[0] &&
            this.location[1] === location[1])
            return;
        this.location = location;
        this.callEventCallback("locationchange", this.location);
        this.parent?.recalculateLocation();
    }
    setMap(map) {
        this.map = map;
    }
    add(child) {
        if (child instanceof Array) {
            child.forEach((c) => {
                this._add(c);
            });
        }
        else {
            this._add(child);
        }
    }
    _add(child) {
        if (this.isInitialized && !child.isInitialized) {
            this.isInitialized = false;
            this.setDontForgetTimeout();
        }
        this.children.push(child);
        child._setParent(this);
        this.recalculateLocation();
        this.callEventCallback("childrenchange", this.children);
    }
    remove(child) {
        const index = this.children.indexOf(child);
        if (index >= 0) {
            this.children[index].parent = undefined;
            // remove from array
            this.children.splice(index, 1);
        }
        else {
            console.log("Child not found.");
        }
        this.recalculateLocation();
        this.callEventCallback("childrenchange", this.children);
    }
    recalculateLocation(children) {
        if (!children)
            children = this.children.map((child) => {
                return child.getLocation();
            });
        if (children.length == 0)
            return;
        let sum = { lat: 0, lng: 0 };
        children.forEach((location) => {
            sum.lat += location[0];
            sum.lng += location[1];
        });
        sum.lat = sum.lat / children.length;
        sum.lng = sum.lng / children.length;
        let finalLocation = [sum.lat, sum.lng];
        if (!this.useAverageLocation) {
            // Find the closest point to the final location
            let closest = children[0];
            let closestDistance = Math.sqrt(Math.pow(finalLocation[0] - closest[0], 2) +
                Math.pow(finalLocation[1] - closest[1], 2));
            for (let i = 1; i < children.length; i++) {
                const child = children[i];
                const distance = Math.sqrt(Math.pow(finalLocation[0] - child[0], 2) +
                    Math.pow(finalLocation[1] - child[1], 2));
                if (distance < closestDistance) {
                    closest = child;
                    closestDistance = distance;
                }
            }
            finalLocation = closest;
        }
        if (this.location[0] === finalLocation[0] &&
            this.location[1] === finalLocation[1])
            return;
        this.setLocation(finalLocation);
    }
    _setParent(parent) {
        if (!parent) {
            console.log("Parent is undefined");
            return;
        }
        else if (this.parent === parent) {
            return;
        }
        else if (this.hasParent) {
            this.parent?.remove(this);
        }
        clearTimeout(this.dontForgetTimeout);
        this.parent = parent;
        this.parent.setMap(this.map);
    }
    setParent(parent) {
        this._setParent(parent);
        if (!this.parent) {
            console.log("Parent is undefined");
            return;
        }
        this.parent.add(this);
    }
    addListener(event, callback, callOnAdd = false) {
        if (!this.eventCallbacks[event])
            this.eventCallbacks[event] = [];
        this.eventCallbacks[event].push(callback);
        if (callOnAdd)
            this.callEventCallback(event, null);
    }
    callEventCallback(event, e) {
        if (!this.eventCallbacks[event])
            return;
        this.eventCallbacks[event].forEach((callback) => {
            callback(e);
        });
    }
    setActive(isActive, force = false, ignoreChildren = false) {
        if (!this.map) {
            console.log("Cannot change active property because map is not attached");
            return;
        }
        if (!force && isActive === this.isActive)
            return;
        this.isActive = isActive;
        if (!ignoreChildren) {
            this.children.forEach((child) => {
                child.setActive(isActive, force);
            });
        }
        if (this.hasParent)
            this.parent?.onChildrenActiveChange();
        this.callEventCallback("activechange", this.isActive);
        return true;
    }
    removeListener(event, callback) {
        if (!this.eventCallbacks[event])
            return;
        const index = this.eventCallbacks[event].indexOf(callback);
        if (index >= 0) {
            this.eventCallbacks[event].splice(index, 1);
        }
    }
    onChildrenActiveChange() {
        let allSame = true;
        let first = true;
        let lastIsActive = true;
        for (let i = 0; i < this.children.length; i++) {
            const l = this.children[i];
            if (!first && lastIsActive != l.isActive)
                allSame = false;
            first = false;
            lastIsActive = l.isActive;
        }
        if (!first && allSame)
            this.setActive(lastIsActive);
    }
    initialize() {
        const inMap = this.isInitialized;
        this.children.forEach((child) => {
            if (!child.isInitialized)
                child.initialize();
        });
        clearTimeout(this.dontForgetTimeout);
        this.isInitialized = true;
        // return inMap !== this.isInitialized;
        return this;
    }
    delete() {
        if (this.hasParent)
            this.parent?.remove(this);
        const childrenArr = [...this.children];
        childrenArr.forEach((child) => {
            child.delete();
        });
        this.isInitialized = false;
    }
    setUseAverageLocation(useAverageLocation) {
        this.useAverageLocation = useAverageLocation;
        this.recalculateLocation();
    }
}
exports.ObjectInMap = ObjectInMap;
